import { useEffect, useState, useMemo } from "react";
import { Util } from "../../../util/Util";
import { useAuth } from "../../../hooks/useAuth";
import { useData } from "../../../hooks/useData";
import { useSessionState } from "../../../hooks/useSessionState";
import { toast } from "react-toastify";
import { useUI } from "../../../providers/MoleUIContext";
import { Box, IconButton, Tooltip } from '@mui/material';
import InventoryReport from "../InventoryReport";
import { LocalStorageKeys, NUMBER_FILTER_OPTIONS, TEXT_FILTER_OPTIONS } from "../../../util/constants";
import { MRT_ColumnDef } from "material-react-table";
import TableView from "../../../components/TableView";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Refresh } from "@mui/icons-material";
import ExportMenuButton from "../../../components/ExportMenuButton";
import Detail from "./Detail";

import { roundQty } from "../../../util/quantity";
const CurrentInventory = () => {
	// Kept for the session: the cached rows belong to this depot, and Refresh needs it.
	const [selectedDepot, setSelectedDepot] = useSessionState<any>(`depot:${LocalStorageKeys.CURRENT_INVENTORY}`, null);
	const [depots, setDepots] = useState<any[]>([]);
	const [isDepotLoading, setIsDepotLoading] = useState<boolean>(false);
	const [params, setParams] = useState({});

	const { api, session } = useAuth();
	const { queueReRun, t } = useUI();
	const { data, lastUpdate, loadFromIndexedDB, saveFromServer, loading, setLoading } = useData(LocalStorageKeys.CURRENT_INVENTORY);

	const handleSelectorChange = (value: Record<string, any>) => {
		setParams(value);
	};

	useEffect(() => {
		loadFromIndexedDB();
	}, []);

	const fetchData = async () => {
		if (!selectedDepot?.id) {
			toast.info(t("common:inventory.selectDepotPrompt"));
			return;
		}
		setLoading(true);

		try {
			const res = await api.get(`inventory/${selectedDepot?.id}`, {
				params: params
			});

			if (res) {
				if (res.status === 200) {
					saveFromServer(res.data, undefined, true);
				} else if (res.status === 401 || res.status === 422) {
					queueReRun(fetchData);
				} else {
					console.error("Failed to fetch data:", res);
				}
			}
		} finally {
			setLoading(false);
		}
	};

	const fetchDepots = async () => {
		setIsDepotLoading(true);

		const res = await api.get("/depot/meterred",
			{
				params: { Scope: "inventory" },
			}
		);

		if (res) {
			if (res.status === 200) {
				setDepots(res.data);
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchDepots);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setIsDepotLoading(false);
	};

	// Define table columns
	const columns = useMemo<MRT_ColumnDef<any>[]>(() => [
		{
			header: t("common:tables.sector"),
			accessorKey: "sector",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.family"),
			accessorKey: "family",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.category"),
			accessorKey: "category",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.itemCode"),
			accessorKey: "item_code",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.item"),
			accessorKey: "item_name",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.stock"),
			accessorKey: "quantity",
			Cell: ({ cell }: { cell: any }) => Util.formatNumber(cell.getValue()),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.unitValue"),
			accessorKey: "amount",
			Cell: ({ cell }: { cell: any }) => Util.formatNumber(cell.getValue()),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.sellingPrice"),
			accessorKey: "total",
			Cell: ({ cell }: { cell: any }) => Util.formatNumber(cell.getValue()),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.inventoryValue"),
			accessorKey: "Value",
			Cell: ({ cell }: { cell: any }) => Util.formatNumber(cell.getValue()),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
			aggregationFn: 'sum', //show the max age in the group (lots of pre-built aggregationFns to choose from)
			//required to render an aggregated cell
			AggregatedCell: ({ cell, table }) => (
				<>
					{t("common:tableView.sumBy")}{' '}
					{table.getColumn(cell.row.groupingColumnId ?? '').columnDef.header}:{' '}
					<Box
						sx={{ color: 'info.main', display: 'inline', fontWeight: 'bold' }}
					>
						{Util.formatNumber(cell.getValue<number>())}
					</Box>
				</>
			),
			// Add Footer for Grand Total
			Footer: ({ table }) => {
				const [total, setTotal] = useState<number>(0);
				const [isCalculating, setIsCalculating] = useState(false);

				useEffect(() => {
					setIsCalculating(true);
					setTotal(0);

					const rows = table.getFilteredRowModel().rows;
					// run in a micro delay, allowing paint before heavy computation
					const handle = setTimeout(() => {
						let sum = 0;
						for (let i = 0; i < rows.length; i++) {
							const value = Number(rows[i].original.Value);
							if (Number.isFinite(value)) sum += value;
						}
						setTotal(roundQty(sum));
						setIsCalculating(false);
					}, 0); // <— yields to browser first

					return () => clearTimeout(handle);
				}, [table.getFilteredRowModel().rows]);

				return (
					<Box sx={{ fontWeight: 'bold', color: 'success.main' }}>
						{t("common:report.total")}: {Util.formatNumber(total)}
					</Box>
				);
			},
		},
		{
			header: t("common:tables.projectedRevenue"),
			accessorKey: "Revenue",
			Cell: ({ cell }: { cell: any }) => Util.formatNumber(cell.getValue()),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
			aggregationFn: 'sum', //show the max age in the group (lots of pre-built aggregationFns to choose from)
			//required to render an aggregated cell
			AggregatedCell: ({ cell, table }) => (
				<>
					{t("common:tableView.sumBy")}{' '}
					{table.getColumn(cell.row.groupingColumnId ?? '').columnDef.header}:{' '}
					<Box
						sx={{ color: 'info.main', display: 'inline', fontWeight: 'bold' }}
					>
						{Util.formatNumber(cell.getValue<number>())}
					</Box>
				</>
			),
			// Add Footer for Grand Total
			Footer: ({ table }) => {
				const [total, setTotal] = useState<number>(0);
				const [isCalculating, setIsCalculating] = useState(false);

				useEffect(() => {
					setIsCalculating(true);
					setTotal(0);

					const rows = table.getFilteredRowModel().rows;
					// run in a micro delay, allowing paint before heavy computation
					const handle = setTimeout(() => {
						let sum = 0;
						for (let i = 0; i < rows.length; i++) {
							const value = Number(rows[i].original.Revenue);
							if (Number.isFinite(value)) sum += value;
						}
						setTotal(roundQty(sum));
						setIsCalculating(false);
					}, 0); // <— yields to browser first

					return () => clearTimeout(handle);
				}, [table.getFilteredRowModel().rows]);

				return (
					<Box sx={{ fontWeight: 'bold', color: 'success.main' }}>
						{t("common:report.total")}: {Util.formatNumber(total)}
					</Box>
				);
			},
		},
		{
			header: t("common:tables.grossProfit"),
			accessorKey: "Profit",
			Cell: ({ cell }: { cell: any }) => Util.formatNumber(cell.getValue()),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
			aggregationFn: 'sum', //show the max age in the group (lots of pre-built aggregationFns to choose from)
			//required to render an aggregated cell
			AggregatedCell: ({ cell, table }) => (
				<>
					{t("common:tableView.sumBy")}{' '}
					{table.getColumn(cell.row.groupingColumnId ?? '').columnDef.header}:{' '}
					<Box
						sx={{ color: 'info.main', display: 'inline', fontWeight: 'bold' }}
					>
						{Util.formatNumber(cell.getValue<number>())}
					</Box>
				</>
			),
			// Add Footer for Grand Total
			Footer: ({ table }) => {
				const [total, setTotal] = useState<number>(0);
				const [isCalculating, setIsCalculating] = useState(false);

				useEffect(() => {
					setIsCalculating(true);
					setTotal(0);

					const rows = table.getFilteredRowModel().rows;
					// run in a micro delay, allowing paint before heavy computation
					const handle = setTimeout(() => {
						let sum = 0;
						for (let i = 0; i < rows.length; i++) {
							const value = Number(rows[i].original.Profit);
							if (Number.isFinite(value)) sum += value;
						}
						setTotal(roundQty(sum));
						setIsCalculating(false);
					}, 0); // <— yields to browser first

					return () => clearTimeout(handle);
				}, [table.getFilteredRowModel().rows]);

				return (
					<Box sx={{ fontWeight: 'bold', color: 'success.main' }}>
						{t("common:report.total")}: {Util.formatNumber(total)}
					</Box>
				);
			},
		},
		{
			header: t("common:tables.grossProfitMargin"),
			accessorKey: "Margin",
			Cell: ({ cell }: { cell: any }) => Util.formatNumber(cell.getValue()),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
			aggregationFn: 'mean', //show the max age in the group (lots of pre-built aggregationFns to choose from)
			//required to render an aggregated cell
			AggregatedCell: ({ cell, table }) => (
				<>
					{t("common:tableView.averageBy")}{' '}
					{table.getColumn(cell.row.groupingColumnId ?? '').columnDef.header}:{' '}
					<Box
						sx={{ color: 'info.main', display: 'inline', fontWeight: 'bold' }}
					>
						{Util.formatNumber(cell.getValue<number>())}
					</Box>
				</>
			),
			// Add Footer for Grand Total
			Footer: ({ table }) => {
				const [total, setTotal] = useState<number>(0);
				const [isCalculating, setIsCalculating] = useState(false);

				useEffect(() => {
					setIsCalculating(true);
					setTotal(0);

					const rows = table.getFilteredRowModel().rows;
					// run in a micro delay, allowing paint before heavy computation
					const handle = setTimeout(() => {
						let sum = 0;
						for (let i = 0; i < rows.length; i++) {
							const value = Number(rows[i].original.Margin);
							if (Number.isFinite(value)) sum += value;
						}
						setTotal(rows.length ? roundQty(sum / rows.length) : 0);
						setIsCalculating(false);
					}, 0); // <— yields to browser first

					return () => clearTimeout(handle);
				}, [table.getFilteredRowModel().rows]);

				return (
					<Box sx={{ fontWeight: 'bold', color: 'success.main' }}>
						{t("common:report.average")}: {Util.formatNumber(total)}
					</Box>
				);
			},
		},
	], [t]);

	return (
		<>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<TableView
					stateKey={LocalStorageKeys.CURRENT_INVENTORY}
					header={t("common:inventory.headers.current")}
					data={data}
					columns={columns}
					includeTimestampColumns={false}
					totals={[
						{ key: "Value", label: t("common:tables.inventoryValue") },
						{ key: "Revenue", label: t("common:tables.projectedRevenue") },
						{ key: "Profit", label: t("common:tables.grossProfit") },
					]}
					lastUpdated={lastUpdate}
					isLoading={loading}
					useDateRangeFilter={false}
					enableRowActions={false}
					filters={<Detail
						selectedDepot={selectedDepot}
						setSelectedDepot={setSelectedDepot}
						setParams={handleSelectorChange}
					/>}

					renderTopToolbarActions={(table) => (
						<Box sx={{ display: 'flex', gap: '0.5rem' }}>
							<Tooltip title={t("common:ui.tooltips.refreshData")}>
								<IconButton onClick={() => fetchData()} color="primary">
									<Refresh />
								</IconButton>
							</Tooltip>
							<ExportMenuButton
								prefix={`current_inventory_${selectedDepot?.DepotName}`}
								data={data}
								columns={columns}
								table={table}
							/>
						</Box>
					)
					}
				/>
			</LocalizationProvider >
		</>
	);
};

export default CurrentInventory;
